import type { Command, EditorState } from 'prosemirror-state';
import type { IntlShape } from 'react-intl';

import { ACTION, ACTION_SUBJECT, EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import type {
	ExtractInjectionAPI,
	FloatingToolbarConfig,
	FloatingToolbarItem,
} from '@atlaskit/editor-common/types';
import type { Node as PMNode, NodeType, Schema } from '@atlaskit/editor-prosemirror/model';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { InlineSuggestionToolbar } from '@atlassian/editor-plugin-ai/InlineSuggestionToolbar';
import { REMIX_DENY_LIST_V1 } from '@atlassian/malleable-ui-components/remix/remix-config';

import type { MalleableUiPlugin } from '../../malleableUiPluginType';
import { malleableUiPluginKey } from '../../pm-plugins/plugin-key';
import { remixThinkingPluginKey } from '../../pm-plugins/remix-thinking/remix-thinking-plugin';
import { messages } from '../messages';
import { ASK_ROVO_MEDIA_NODE_TYPES, isConsideredEmptyForRemix } from '../remix-config';

import {
	buildTypeSwitcherDropdownItems,
	getMediaFileIdFromAskRovoNode,
} from './floating-toolbar/floating-toolbar-remix-variant';

export function getUnallowedNodes(schema: Schema): Set<NodeType> {
	return new Set(REMIX_DENY_LIST_V1.map((name) => schema.nodes[name]).filter(Boolean));
}

export function getMediaNodesForAskRovo(schema: Schema): Set<NodeType> {
	return new Set(ASK_ROVO_MEDIA_NODE_TYPES.map((name) => schema.nodes[name]).filter(Boolean));
}

const getMediaNodeForAskRovo = (node: PMNode): PMNode | undefined => {
	if (node.type.name === 'media') {
		return node;
	}
	if (node.type.name === 'mediaSingle') {
		return node.firstChild ?? undefined;
	}
	return undefined;
};

const isImageMediaNodeForAskRovo = (node: PMNode): boolean => {
	const mediaNode = getMediaNodeForAskRovo(node);
	if (!mediaNode || mediaNode.type.name !== 'media') {
		return false;
	}

	if (mediaNode.attrs?.type === 'external') {
		return true;
	}

	const fileMimeType = mediaNode.attrs?.__fileMimeType;
	return typeof fileMimeType === 'string' && fileMimeType.includes('image/');
};

const remixFloatingToolbarClickAnalyticsPayload = {
	action: ACTION.CLICKED,
	actionSubject: ACTION_SUBJECT.BUTTON,
	actionSubjectId: 'remixModalFromToolbar',
	eventType: EVENT_TYPE.UI,
	source: 'remix',
} as const;

export function getOverriddenFloatingToolbarConfig(
	api?: ExtractInjectionAPI<MalleableUiPlugin>,
): (
	state: EditorState,
	intl: IntlShape,
	_providerFactory: unknown,
	activeConfigs?: Array<FloatingToolbarConfig>,
) => FloatingToolbarConfig | undefined {
	if (!api?.toolbar) {
		return () => undefined;
	}

	return (
		state: EditorState,
		intl: IntlShape,
		_providerFactory: unknown,
		activeConfigs?: Array<FloatingToolbarConfig>,
	): FloatingToolbarConfig | undefined => {
		if (!activeConfigs?.length) {
			return;
		}

		const unallowedNodes = getUnallowedNodes(state.schema);
		const malleableUiState = malleableUiPluginKey.getState(state);
		const remixThinkingState = remixThinkingPluginKey.getState(state);
		const isRemixing =
			malleableUiState?.isRemixModalOpen || false || remixThinkingState?.isThinking || false;
		const areAiEntryPointsDisabled =
			api?.aiExperience?.sharedState.currentState()?.areAiEntryPointsDisabled ?? false;
		activeConfigs.forEach((config) => {
			const configNodeTypes = ([] as NodeType[]).concat(config.nodeType);
			const isTableConfig = !Array.isArray(config.nodeType) && config.nodeType.name === 'table';

			// When an element is being remixed (modal open or thinking bar showing), hide its
			// element/floating toolbar entirely by emptying its items so the floating toolbar plugin
			// won't show it.
			//
			// Behind `cc-maui-phase-3` this applies to every element type. Previously the toolbar
			// was only hidden for tables, which left the element toolbar visible for other nodes
			// (e.g. panels and column layouts) while the thinking bar was showing. We skip the
			// inline-suggestion toolbar so the in-editor remix loading bar itself is not affected.
			// When the gate is off we retain the legacy table-only behaviour below.
			if (
				isRemixing &&
				config.className !== undefined &&
				config.className !== InlineSuggestionToolbar.INLINE_SUGGESTION_FLOATING_TOOLBAR_CLASS &&
				fg('cc-maui-phase-3')
			) {
				config.items = [];
				return;
			}

			// Legacy behaviour (gate off): hide only the table element toolbar while remixing.
			if (isTableConfig && isRemixing && !fg('cc-maui-phase-3')) {
				config.items = [];
				return;
			}

			if (
				(config.className !== undefined &&
					config.className === InlineSuggestionToolbar.INLINE_SUGGESTION_FLOATING_TOOLBAR_CLASS) ||
				(isTableConfig && config.items.length === 0) ||
				configNodeTypes.some((nodeType) => unallowedNodes.has(nodeType) || nodeType.isInline)
			) {
				return;
			}

			const toolbarItems = config.items;
			if (Array.isArray(toolbarItems)) {
				const remixButton: FloatingToolbarItem<Command> = {
					id: 'editor.malleable-ui.remix',
					type: 'button',
					title: intl.formatMessage(messages.remixToolbarLabel),
					tooltipContent: isRemixing
						? intl.formatMessage(messages.remixInProgressTooltip)
						: intl.formatMessage(messages.remixToolbarTooltip),
					showTitle: true,
					hideTooltipOnClick: false,
					icon: AiGenerativeRemixIcon,
					onClick: () => false,
					disabled: isRemixing || areAiEntryPointsDisabled,
					testId: 'editor-floating-toolbar__remix-button',
				};
				const remixToolbarItems: FloatingToolbarItem<Command>[] = [
					remixButton,
					{
						type: 'separator',
						fullHeight: true,
					},
				];

				config.items = (node) => {
					remixButton.onClick = (editorState) => {
						if (isRemixing) {
							return false;
						}
						const $pos = editorState.selection.$from;
						let nodePos: number | undefined;
						for (let depth = $pos.depth; depth > 0; depth--) {
							if ($pos.node(depth) === node) {
								nodePos = $pos.before(depth);
								break;
							}
						}
						api.analytics?.actions.fireAnalyticsEvent<
							typeof remixFloatingToolbarClickAnalyticsPayload,
							'customEventType'
						>(remixFloatingToolbarClickAnalyticsPayload);
						api.core.actions.execute(
							api.malleableUi.commands.openRemixModal({
								nodePos,
								nodeType: node.type.name,
								metadata: {
									interactionSource: 'remixModalFromFloatingToolbar',
								},
								source: 'floatingToolbar',
							}),
						);
						return true;
					};
					remixButton.disabled =
						isRemixing || areAiEntryPointsDisabled || isConsideredEmptyForRemix(node);
					return [...remixToolbarItems, ...toolbarItems];
				};
			}
		});

		const mediaNodesForAskRovo = getMediaNodesForAskRovo(state.schema);
		const isViewMode = api?.editorViewMode?.sharedState.currentState()?.mode === 'view';
		const showAskRovoForMedia =
			fg('cc-maui-phase-2') ||
			(isViewMode && expValEquals('cc-maui-experiment', 'isEnabled', true));
		if (!mediaNodesForAskRovo.size || !showAskRovoForMedia) {
			return;
		}

		const askRovoButton = api?.aiExperience?.actions?.getAskRovoFloatingToolbarButton?.({
			intl,
			disabled:
				api?.malleableUi?.actions?.shouldDisableRemix?.() && fg('platform_editor_ai_remix_toolbar'),
			target: 'image',
		});
		if (!askRovoButton) {
			return;
		}

		const shouldShowAskRovoForMediaNode = (node: PMNode) =>
			mediaNodesForAskRovo.has(node.type) &&
			!node.type.isInline &&
			isImageMediaNodeForAskRovo(node);

		const buildTypeSwitcherItemsForMediaNode = (
			node: PMNode,
		): FloatingToolbarItem<Command> | undefined => {
			return buildTypeSwitcherDropdownItems({
				node,
				schema: state.schema,
				state,
				intl,
				disabled: Boolean(isRemixing || areAiEntryPointsDisabled),
				api,
			});
		};

		// Build a per-node wrapped Ask Rovo button whose onClick stages the
		// selected media's `mediaFileId` as the inline-Rovo streaming
		// target.
		const buildAskRovoItemsForMediaNode = (node: PMNode): FloatingToolbarItem<Command>[] => {
			const wrappedAskRovoButton: FloatingToolbarItem<Command> = {
				...askRovoButton,
				...(isViewMode ? { supportsViewMode: true } : {}),
				onClick: (innerState, dispatch, view) => {
					if (fg('cc-maui-phase-2')) {
						const mediaFileId = getMediaFileIdFromAskRovoNode(node);
						if (mediaFileId) {
							api?.aiExperience?.actions?.stageInlineRovoStreaming?.(mediaFileId);
						}
					}
					return askRovoButton.onClick ? askRovoButton.onClick(innerState, dispatch, view) : false;
				},
			};
			return [
				wrappedAskRovoButton,
				{
					type: 'separator',
					fullHeight: true,
					...(isViewMode ? { supportsViewMode: true } : {}),
				},
			];
		};

		activeConfigs.forEach((config) => {
			const nodeTypes = ([] as NodeType[]).concat(config.nodeType);
			const isMediaConfig = nodeTypes.some(
				(nodeType) => mediaNodesForAskRovo.has(nodeType) && !nodeType.isInline,
			);
			if (!isMediaConfig) {
				return;
			}

			const items = config.items;

			if (Array.isArray(items)) {
				config.items = (node) => {
					if (!shouldShowAskRovoForMediaNode(node)) {
						return items;
					}
					const withAskRovo = [...buildAskRovoItemsForMediaNode(node), ...items];
					if (fg('platform_editor_ai_remix_toolbar')) {
						const typeSwitcher = buildTypeSwitcherItemsForMediaNode(node);
						if (typeSwitcher) {
							withAskRovo.splice(1, 0, typeSwitcher);
						}
					}
					return withAskRovo;
				};
			} else if (typeof items === 'function') {
				config.items = (node) => {
					const baseItems = items(node);
					if (!shouldShowAskRovoForMediaNode(node)) {
						return baseItems;
					}
					const withAskRovo = [...buildAskRovoItemsForMediaNode(node), ...baseItems];
					if (fg('platform_editor_ai_remix_toolbar')) {
						const typeSwitcher = buildTypeSwitcherItemsForMediaNode(node);
						if (typeSwitcher) {
							withAskRovo.splice(1, 0, typeSwitcher);
						}
					}
					return withAskRovo;
				};
			}
		});
	};
}
